type NodeChain = {
    element: HTMLMediaElement;
    source: MediaElementAudioSourceNode;
    gain: GainNode;
};

export class AudioNodePool {
    private context: AudioContext | null = null;
    private readonly sourceByElement = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();
    private readonly chainByKey = new Map<string, NodeChain>();

    private getContext(): AudioContext {
        if (!this.context) {
            this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        return this.context;
    }

    /**
     * 同步建立 MediaElementSource → Gain → destination。
     * 不在此处调用 context.resume()：被动挂载 / 数据拉取链上调用会触发 Chrome 自动播放策略警告且无手势无法启动。
     * 请在用户手势（播放、点按 BGM、音量确认等）后单独 resume AudioContext。
     */
    ensureMediaGainNode(
        key: string,
        element: HTMLMediaElement,
        initialGain: number
    ): { context: AudioContext; gain: GainNode; source: MediaElementAudioSourceNode } {
        const context = this.getContext();

        const existing = this.chainByKey.get(key);
        if (existing && existing.element === element) {
            existing.gain.gain.setValueAtTime(initialGain, context.currentTime);
            return { context, gain: existing.gain, source: existing.source };
        }

        if (existing) {
            try {
                existing.source.disconnect();
            } catch {
                // ignore
            }
            try {
                existing.gain.disconnect();
            } catch {
                // ignore
            }
        }

        let source = this.sourceByElement.get(element);
        if (!source) {
            source = context.createMediaElementSource(element);
            this.sourceByElement.set(element, source);
        }

        const gain = context.createGain();
        gain.gain.setValueAtTime(initialGain, context.currentTime);
        source.connect(gain);
        gain.connect(context.destination);

        this.chainByKey.set(key, { element, source, gain });
        return { context, gain, source };
    }

    getContextRef(): AudioContext | null {
        return this.context;
    }

    getGainNode(key: string): GainNode | null {
        return this.chainByKey.get(key)?.gain || null;
    }

    async disposeAll(closeContext: boolean = false): Promise<void> {
        for (const chain of this.chainByKey.values()) {
            try {
                chain.source.disconnect();
            } catch {
                // ignore
            }
            try {
                chain.gain.disconnect();
            } catch {
                // ignore
            }
        }
        this.chainByKey.clear();

        if (closeContext && this.context) {
            try {
                await this.context.close();
            } catch {
                // ignore
            }
            this.context = null;
        }
    }
}

